<script setup lang="ts">
import {
  computed,
  onMounted,
  provide,
  reactive,
  ref,
  watch,
} from 'vue';
import { useRouter } from 'vue-router';
import SwProductListingFilter from './SwProductListingFilter.vue';
import {
  CmsElementProductListing,
  CmsElementSidebarFilter,
  useListing,
} from '@shopware-pwa/composables-next';
import type { ListingFilter } from '@shopware-pwa/types';
import deepMerge from '../helpers/deepMerge';
import getTranslations from '../helpers/getTranslations';
import { omitQuery } from '../helpers/route/queryHelpers';
import { Button, Loader, Modal, Icon, Search } from '@upa/design-system';

// Define the interface for the filter option here
interface FilterOption {
  code: string;
  value: string | number | boolean;
}

// Define the interface for the translations here
interface Translations {
  listing: {
    sort: string;
    resetFilters: string;
    [key: string]: any;
  };
  [key: string]: any;
}

const { isProfessionalStore } = useStore();

const props = defineProps<{
  content: CmsElementProductListing | CmsElementSidebarFilter;
  listingType?: string;
}>();

// Translations setup
const defaultTranslations: Translations = {
  listing: {
    sort: 'Sort',
    resetFilters: 'Reset filters',
  },
};
const translations = deepMerge(defaultTranslations, getTranslations()) as Translations;

const route = useRoute();
const router = useRouter();

// UI state
const isSortMenuOpen = ref(true);
const dropdownElement = ref(null);
const opened1 = ref(false);

// Listing loading
const { setLoading } = useListingLoading();

// Listing setup for category
const {
  search: searchListing,
  // getCurrentSortingOrder,
  changeCurrentSortingOrder,
  // getSortingOrders,
  // getInitialFilters,
  // getCurrentFilters,
  filtersToQuery,
  setInitialListing,
  getElements: products,
} = useListing({ listingType: 'categoryListing' });

// Listing setup for search results
const { search, getCurrentListing } = useListing({ listingType: 'productSearchListing' });

// Listing filters
const { initialFilters, currentFilters } = useListingFilters();

// Listing sortings
const { initialSortingOptions, initialSortingValue } = useListingSorting();

// Search handling
const queryString = ref('');
const searchKey = ref(0); // Key only for reset, not for normal inputs
const searchRef = ref<InstanceType<typeof Search> | null>(null);

// Filter state management
const sidebarSelectedFilters = reactive({
  manufacturer: new Set<string>(),
  properties: new Set<string>(),
  'min-price': undefined as string | undefined,
  'max-price': undefined as string | undefined,
  rating: undefined as string | undefined,
  'shipping-free': undefined as boolean | undefined,
});

// Computed values
const showSorting = computed(() => initialSortingOptions?.value?.length > 1);

const searchProducts = computed(() => {
  const items: { id: string; productName: string; superTitle: string }[] = [];
  products.value?.forEach((element) => {
    items.push({
      id: element.id,
      productName: element.translated?.name || '',
      superTitle: '',
    });
  });
  return items.length > 0 ? items : [{ id: '', productName: 'No products found', superTitle: '' }];
});

const searchCriteriaForRequest = computed(() => ({
  manufacturer: [...sidebarSelectedFilters.manufacturer]?.join('|'),
  properties: [...sidebarSelectedFilters.properties]?.join('|'),
  'min-price': sidebarSelectedFilters['min-price'],
  'max-price': sidebarSelectedFilters['max-price'],
  order: initialSortingValue.value,
  'shipping-free': sidebarSelectedFilters['shipping-free'],
  rating: sidebarSelectedFilters['rating'],
}));

const filtersToDisplay: ComputedRef<ListingFilter[] | []> = computed(() => {
  // If search is active, use filters from search results
  // if (queryString.value && queryString.value.length >= 3) {
  //   const aggregations = getCurrentListing.value?.aggregations;
  //   // console.log('aggregations', aggregations);
  // }
  // Otherwise show category filters
  return initialFilters.value || [];
});

const selectedOptionIds = computed(() => [
  ...sidebarSelectedFilters.properties,
  ...sidebarSelectedFilters.manufacturer,
]);

const currentSortLabel = computed(() => {
  const sortingObject = initialSortingOptions.value?.find(
    (obj) => obj.key === initialSortingValue.value
  );
  return sortingObject?.label || '';
});

const currentSortingOrder = computed({
  get: () => initialSortingValue.value || '',
  set: async (order) => {
    initialSortingValue.value = order;

    // Create the URL parameters based on the current parameters
    const urlParams = new URLSearchParams();

    // Take over existing parameters from route.query
    for (const key in route.query) {
      if (key !== 'order' && route.query[key]) {
        urlParams.set(key, route.query[key] as string);
      }
    }

    // Add the sorting parameter
    urlParams.set('order', order);

    const queryWithoutOrder = omitQuery(route.query, 'order');

    // Change the sorting order
    if (queryString.value) {
      setLoading(true);
      await search({
        ...Object.fromEntries(urlParams.entries()),
        limit: 20
      });
      if (getCurrentListing.value) {
        setInitialListing(getCurrentListing.value);
      }
      setLoading(false);
    } else {
      setLoading(true);
      await changeCurrentSortingOrder(order, {
        ...queryWithoutOrder,
        limit: 20
      });
      setLoading(false);
    }

    // Navigate using router.replace if no query parameter is present, otherwise refresh
    // if (!route.query.query) {
    //   router.replace({ path: window.location.pathname, query: Object.fromEntries(urlParams.entries()) });
    // } else {
    //   const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
    //   window.location.href = newUrl;
    // }

    router.push({ query: Object.fromEntries(urlParams.entries()) });
  },
});

// Filter hiding configuration
const filtersToBeHiddenById = isProfessionalStore
  ? []
  : ['416f443defbfbd074117efbfbd0c295c', '018c20895b7073f7a6f64e5dcab88a05'];

// Provide selected filters to child components
provide('selectedOptionIds', selectedOptionIds);

// Methods
const performSearch = async (query: string) => {
  if (query.length >= 3) {
    setLoading(true);

    await search({
      limit: 20,
      p: 1,
      query: query,
      properties: route.query.properties ? String(route.query.properties) : undefined,
      order: route.query.order ? String(route.query.order) : undefined
    });

    if (getCurrentListing.value) {
      setInitialListing(getCurrentListing.value);
    }

    setLoading(false);
  }
};

const searchQuery = (query: string) => {
  queryString.value = query;
};

const showAllResults = async () => {
  // If the search term is empty, remove the query parameter from the URL
  // and apply only the filters
  if (!queryString.value || queryString.value.length === 0) {
    // console.log('Search query is empty, removing query parameter');

    // Create the URL parameters based on the existing filters
    const urlParams = new URLSearchParams();

    // Take over existing parameters from route.query, except 'query'
    for (const key in route.query) {
      if (key !== 'query' && route.query[key]) {
        urlParams.set(key, route.query[key] as string);
      }
    }

    // Force a full page refresh without the query parameter
    // const newUrl = `${window.location.pathname}${urlParams.toString() ? '?' + urlParams.toString() : ''}`;
    // console.log('Navigating to URL without search query:', newUrl);
    // window.location.href = newUrl;
    router.push({ query: Object.fromEntries(urlParams.entries()) });
    return;
  }

  // If the search term has at least 3 characters, perform the normal search
  if (queryString.value.length >= 3) {
    // Update the search
    // await performSearch(queryString.value);

    // Create the URL parameters based on the current search term and route.query
    const urlParams = new URLSearchParams();

    // Take over existing parameters from route.query
    for (const key in route.query) {
      if (key !== 'query' && key !== 'p' && route.query[key]) {
        urlParams.set(key, route.query[key] as string);
      }
    }

    // Add the search term
    urlParams.set('query', queryString.value);

    // Force a full page refresh
    // const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
    // console.log('Navigating to search URL:', newUrl);
    // window.location.href = newUrl;
    router.push({ query: Object.fromEntries(urlParams.entries()) });
  } else {
    // console.log('Search query is too short (< 3 characters)');
  }
};

const onOptionSelectToggle = async ({ code, value }: FilterOption) => {
  // console.log('Filter selected:', { code, value });
  // console.log('Current queryString:', queryString.value);
  // console.log('Current route.query:', route.query);

  if (!['properties', 'manufacturer'].includes(code)) {
    (sidebarSelectedFilters as any)[code] = value;
  } else {
    const filterSet = (sidebarSelectedFilters as any)[code] as Set<string>;
    if (filterSet?.has(value as string)) {
      filterSet?.delete(value as string);
    } else {
      filterSet?.add(value as string);
    }
  }

  // Merged search criteria with current query if exists
  const searchParams = {
    ...searchCriteriaForRequest.value,
    ...(queryString.value ? { query: queryString.value } : {})
  };

  // console.log('Search params before search:', searchParams);
  if (queryString.value) {
    setLoading(true);
    await search({
      ...searchParams,
      limit: 20
    });
    if (getCurrentListing.value) {
      setInitialListing(getCurrentListing.value);
    }
    setLoading(false);
  } else {
    setLoading(true);
    await searchListing({
      ...searchParams,
      limit: 20
    });
    setLoading(false);
  }

  // Direct adoption of the query parameter from the current route, if present
  const currentQuery = route.query.query as string | undefined;

  // Make sure to keep the query parameter in URL
  const queryParams = {
    ...filtersToQuery(searchCriteriaForRequest.value)
  };

  // Prioritize the current search term from the URL, otherwise the one from queryString
  // Removal of the condition that the search term must be at least 3 characters long
  if (currentQuery) {
    queryParams.query = currentQuery;
    // console.log('Using query from URL:', currentQuery);
  } else if (queryString.value) {
    queryParams.query = queryString.value;
    // console.log('Using query from state:', queryString.value);
  }

  // console.log('Final queryParams for router.push:', queryParams);

  // Create the full URL
  const urlParams = new URLSearchParams();
  for (const key in queryParams) {
    if (queryParams[key] !== undefined && queryParams[key] !== '') {
      urlParams.set(key, queryParams[key]);
    }
  }

  // const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
  // console.log('Forcing navigation to:', newUrl);

  // Navigate using router.replace if no query parameter is present, otherwise refresh
  // if (!route.query.query) {
  //   router.replace({ path: window.location.pathname, query: queryParams });
  // } else {
  //   window.location.href = newUrl;
  // }

  router.push({ query: Object.fromEntries(urlParams.entries()) });
};

const clearFilters = async () => {
  // console.log('Clearing filters');
  sidebarSelectedFilters.manufacturer.clear();
  sidebarSelectedFilters.properties.clear();
  sidebarSelectedFilters['min-price'] = undefined;
  sidebarSelectedFilters['max-price'] = undefined;
  sidebarSelectedFilters['rating'] = undefined;
  sidebarSelectedFilters['shipping-free'] = undefined;

  // await searchListing(searchCriteriaForRequest.value);

  // Create the URL parameters based on the current search term (if present)
  const queryParams: Record<string, string> = {};

  // Add the search term only if one exists and is not empty
  if (queryString.value && queryString.value.length >= 3) {
    queryParams.query = queryString.value;
  }

  // Create the full URL
  const urlParams = new URLSearchParams();
  for (const key in queryParams) {
    if (queryParams[key] !== undefined && queryParams[key] !== '') {
      urlParams.set(key, queryParams[key]);
    }
  }

  // Create either a URL with parameters or without parameters
  // const newUrl = urlParams.toString()
  //   ? `${window.location.pathname}?${urlParams.toString()}`
  //   : window.location.pathname;

  // Navigate using router.replace if no query parameter is present, otherwise refresh
  // if (!route.query.query) {
  //   router.replace({ path: window.location.pathname, query: queryParams });
  // } else {
  //   window.location.href = newUrl;
  // }

  router.push({ query: Object.fromEntries(urlParams.entries()) });
};

const invokeCleanFilters = async () => {
  // Clear the search field
  queryString.value = '';
  // Only remount component on reset
  searchKey.value++;

  setLoading(true);
  await searchListing({order: currentSortingOrder.value, limit: 20});
  setLoading(false);
  clearFilters();
};

const toggleModal = () => {
  opened1.value = !opened1.value;
};

const closeFilterModal = () => {
  opened1.value = false;
};

// Watchers
watch(queryString, useDebounceFn(async (value) => {
  // console.log('watch value', value);
  if (browserNavigation.value) {
    return;
  }
  if (value.length >= 3) {
    await performSearch(value);
  }
}, 300));

// Lifecycle hooks
onMounted(async () => {
  // Initialize queryString from URL if present
  if (route.query.query) {
    queryString.value = route.query.query as string;
    // await performSearch(queryString.value);
  }

  // Initialize filters from URL parameters
  initializeFiltersFromUrl();

  onClickOutside(dropdownElement, () => (isSortMenuOpen.value = false));
});

// Initialize filters from URL parameters
const initializeFiltersFromUrl = () => {
  // console.log('Initializing filters from URL parameters:', route.query);

  // Set manufacturer filters from the "manufacturer" URL parameter
  if (route.query.manufacturer) {
    const manufacturers = (route.query.manufacturer as string).split('|');
    manufacturers.forEach(id => {
      if (id && id.trim()) {
        sidebarSelectedFilters.manufacturer.add(id.trim());
      }
    });
    // console.log('Initialized manufacturer filters:', [...sidebarSelectedFilters.manufacturer]);
  }

  // Set property filters from the "properties" URL parameter
  if (route.query.properties) {
    const properties = (route.query.properties as string).split('|');
    properties.forEach(id => {
      if (id && id.trim()) {
        sidebarSelectedFilters.properties.add(id.trim());
      }
    });
    // console.log('Initialized properties filters:', [...sidebarSelectedFilters.properties]);
  }

  // Set other filters (min-price, max-price, rating, shipping-free)
  if (route.query['min-price']) {
    sidebarSelectedFilters['min-price'] = route.query['min-price'] as string;
  }

  if (route.query['max-price']) {
    sidebarSelectedFilters['max-price'] = route.query['max-price'] as string;
  }

  if (route.query.rating) {
    sidebarSelectedFilters.rating = route.query.rating as string;
  }

  if (route.query['shipping-free']) {
    sidebarSelectedFilters['shipping-free'] =
      route.query['shipping-free'] === 'true' ||
      route.query['shipping-free'] === '1';
  }
}

// Indicates whether the user navigated using the browser's back or forward buttons
const browserNavigation = ref(false);

useListingNavigation(async () => {
  browserNavigation.value = true;

  sidebarSelectedFilters.manufacturer.clear();
  sidebarSelectedFilters.properties.clear();
  sidebarSelectedFilters['min-price'] = undefined;
  sidebarSelectedFilters['max-price'] = undefined;
  sidebarSelectedFilters['rating'] = undefined;
  sidebarSelectedFilters['shipping-free'] = undefined;

  initializeFiltersFromUrl();

  const searchValue = String(route.query.query ?? '');
  if (queryString.value === searchValue) {
    browserNavigation.value = false;
    return;
  }
  queryString.value = searchValue;

  // Call the exposed method inside <Search>
  // @ts-ignore
  searchRef.value?.setSearchValue(searchValue);
});
</script>

<template>
  <ClientOnly>
    <div class="bg-white">
      <main class="product-listing-filters">
        <!-- Mobile filter trigger button -->
        <div class="filter-sidebar-trigger">
          <Button :primary="true" @click="toggleModal">
            {{ $t('listing.filter') }}
          </Button>

          <!-- Mobile filter modal -->
          <Modal
            v-if="opened1"
            class="oc-sidebar__modal oc-sidebar__modal_left"
            :title="$t('listing.modalTitle')"
            @layer-click="closeFilterModal"
            @escape-press="closeFilterModal"
            @close="closeFilterModal"
          >
            <template #content>
              <div v-if="filtersToDisplay.length" class="flex flex-wrap">
                <!-- Search component -->
                <div class="fiter-sidebar__search-container">
                  <Search
                    ref="searchRef"
                    :key="`mobile-search-${searchKey}`"
                    :search-preview="true"
                    :product-list="searchProducts"
                    :search-placeholder="$t('listing.search.placeholder')"
                    :value="queryString"
                    @search="showAllResults"
                    @go-to-result="showAllResults"
                    @search-query-input="searchQuery"
                    @keyup.enter="showAllResults"
                  />
                </div>

                <!-- Filters list -->
                <div
                  v-for="filter in filtersToDisplay.filter((item) => item.id)"
                  :key="`${filter?.id || filter?.code}`"
                  :class="{
                    'hide-filters': filtersToBeHiddenById.includes(filter.id),
                  }"
                  class="mb-2 w-full"
                >
                  <SwProductListingFilter
                    :selected-filters="currentFilters"
                    :filter="filter"
                    class="relative"
                    @selectFilterValue="onOptionSelectToggle"
                  />
                </div>

                <!-- Reset filters button -->
                <Button
                  type="button"
                  :primary="true"
                  :full-width="true"
                  @click="invokeCleanFilters"
                >
                  {{ $t('listing.resetFilters') }}
                </Button>

                <!-- Mobile sorting dropdown -->
                <div
                  v-if="showSorting"
                  ref="dropdownElement"
                  class="sorting-select-mobile"
                  style="width: 100%; position: relative"
                >
                  <div>
                    <button
                      id="menu-button"
                      type="button"
                      style="
                        height: 76px;
                        width: 100%;
                        border-bottom: 1px solid #8d8f9a;
                        padding-left: 16px;
                        margin-top: 24px;
                        position: relative;
                      "
                      :style="{
                        backgroundColor: isSortMenuOpen ? 'white' : '#f1f2f3',
                        boxShadow: isSortMenuOpen
                          ? 'rgba(0, 0, 0, 0.25) 0px 0px 12px'
                          : 'none',
                      }"
                      aria-expanded="false"
                      aria-haspopup="true"
                      @click="isSortMenuOpen = !isSortMenuOpen"
                    >
                      <div
                        style="display: flex; font-size: 14px"
                        :style="{
                          backgroundColor: isSortMenuOpen ? 'white' : '#f1f2f3',
                        }"
                      >
                        {{ translations.listing.sort }}
                      </div>
                      <div style="display: flex; font-size: 16px">
                        {{ currentSortLabel }}
                        <div style="justify-self: flex-end; margin-left: auto; margin-right: 16px;">
                          <Icon
                            name="chevron_down"
                            :size="400"
                            :class="{ hidden: isSortMenuOpen }"
                          />
                          <Icon
                            name="chevron_up"
                            :size="400"
                            :class="{ hidden: !isSortMenuOpen }"
                          />
                        </div>
                      </div>
                    </button>
                  </div>

                  <!-- Sorting options dropdown menu -->
                  <div
                    :class="[isSortMenuOpen ? 'relative' : 'hidden']"
                    role="menu"
                    aria-orientation="vertical"
                    aria-labelledby="menu-button"
                    tabindex="-1"
                    style="width: 100%"
                  >
                    <div
                      role="none"
                      style="
                        background-color: white;
                        width: 100%;
                        z-index: 1;
                        box-shadow: rgba(0, 0, 0, 0.25) 0px 0px 12px;
                      "
                    >
                      <button
                        v-for="sorting in initialSortingOptions"
                        :key="sorting.key"
                        style="
                          width: 100%;
                          text-align: left;
                          padding: 8px;
                          border-bottom: 1px solid #dbdbdb;
                          background-color: white;
                          font-size: 12px;
                        "
                        :style="{
                          color:
                            sorting.key === initialSortingValue
                              ? '#da202c'
                              : '#72757e',
                        }"
                        role="menuitem"
                        tabindex="-1"
                        @click="
                          currentSortingOrder = sorting.key;
                          isSortMenuOpen = false;
                        "
                      >
                        {{ sorting.label }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </Modal>
        </div>

        <!-- Desktop filter sidebar -->
        <div v-if="filtersToDisplay.length" class="flex flex-wrap filter-desktop">
          <!-- Desktop search component -->
          <div>
            <Search
              ref="searchRef"
              :key="`desktop-search-${searchKey}`"
              :search-preview="true"
              :product-list="searchProducts"
              :search-placeholder="$t('listing.search.placeholder')"
              :value="queryString"
              @search="showAllResults"
              @go-to-result="showAllResults"
              @search-query-input="searchQuery"
              @keydown.enter="showAllResults"
            />
          </div>

          <!-- Desktop filters list -->
          <div
            v-for="filter in filtersToDisplay.filter((item) => item.id)"
            :key="`${filter?.id || filter?.code}`"
            :class="{
              'hide-filters': filtersToBeHiddenById.includes(filter.id),
            }"
            class="mb-2 w-full"
          >
            <SwProductListingFilter
              :selected-filters="currentFilters"
              :filter="filter"
              class="relative"
              @selectFilterValue="onOptionSelectToggle"
            />
          </div>

          <!-- Desktop reset filters button -->
          <Button
            class="menu-button__filter"
            :outline="true"
            :icon-name="'close'"
            :content="$t('listing.resetFilters')"
            @click="invokeCleanFilters"
          />
        </div>

        <!-- Desktop sorting dropdown -->
        <div
          v-if="showSorting"
          ref="dropdownElement"
          class="sorting-select-desktop"
          style="position: relative"
        >
          <div>
            <button
              id="menu-button"
              type="button"
              class="menu-button__sorting"
              aria-expanded="false"
              aria-haspopup="true"
              @click="isSortMenuOpen = !isSortMenuOpen"
            >
              <div style="display: flex; font-size: 14px">
                {{ translations.listing.sort }}
              </div>
              <div style="display: flex; font-size: 16px">
                {{ currentSortLabel }}
                <div style="justify-self: flex-end; margin-left: auto; margin-right: 16px;">
                  <Icon
                    name="chevron_down"
                    :size="400"
                    :class="{ hidden: isSortMenuOpen }"
                  />
                  <Icon
                    name="chevron_up"
                    :size="400"
                    :class="{ hidden: !isSortMenuOpen }"
                  />
                </div>
              </div>
            </button>
          </div>

          <!-- Sorting options dropdown -->
          <div
            :class="[isSortMenuOpen ? 'relative' : 'hidden']"
            role="menu"
            aria-orientation="vertical"
            aria-labelledby="menu-button"
            tabindex="-1"
          >
            <div
              role="none"
              style="
                background-color: white;
                width: 100%;
                z-index: 1;
                box-shadow: rgba(0, 0, 0, 0.25) 0px 0px 12px;
              "
            >
              <button
                v-for="sorting in initialSortingOptions"
                :key="sorting.key"
                style="
                  width: 100%;
                  text-align: left;
                  padding: 8px;
                  border-bottom: 1px solid #dbdbdb;
                  background-color: white;
                  font-size: 12px;
                "
                :style="{
                  color:
                    sorting.key === initialSortingValue
                      ? '#da202c'
                      : '#72757e',
                }"
                role="menuitem"
                tabindex="0"
                @click="
                  currentSortingOrder = sorting.key;
                  isSortMenuOpen = false;
                "
              >
                {{ sorting.label }}
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>

    <!-- Placeholder/Loading state -->
    <template #placeholder>
      <div class="bg-white">
        <main class="mx-auto">
          <div style="height: 400px">
            <div
              class="relative flex items-center justify-around pt-6 pb-6"
              style="height: 100%"
            >
              <Loader :loading="true" />
            </div>
            <div
              v-show="!initialFilters.length"
              class="flex flex-wrap"
            ></div>
          </div>
        </main>
      </div>
    </template>
  </ClientOnly>
</template>

<style scoped lang="scss">
.fiter-sidebar__search-container {
  width: 100%;
}

.product-listing-filters {
  --focus-padding: 20px;
  padding-inline: var(--focus-padding);

  @media (min-width: 1024px) {
    padding-bottom: 100px;
  }

  .hide-filters {
    display: none;
  }
}
.filter-sidebar-trigger {
  display: inline-block;
  max-width: fit-content;
  position: absolute;
  right: 16px;
  margin-top: 24px;

  @media (min-width: 1024px) {
    display: none;
  }
}

.sorting-select-desktop {
  display: none;

  @media (min-width: 1024px) {
    display: block;
  }
}

.filter-desktop {
  display: none;

  @media (min-width: 1024px) {
    display: block;
  }
}

.button__wrapper {
  width: 100%;
}

:deep(.modal__container) {
  padding: 16px;
}

:deep(.modal__content) {
  margin-top: 16px;
}

.menu-button__sorting {
  position: relative;
  height: 76px;
  width: 100%;
  border: 1px solid #8d8f9a;
  border-radius: 6px;
  padding-left: 16px;
  margin-top: 24px;
  background-color: var(--_c-white-primary);

  .icon {
    position: absolute;
    top: 50%;
    right: 16px;
    transform: translateY(-50%);
  }
}

.menu-button__filter {
  :deep(.button) {
    width: 100%;
  }
}
</style>